import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { SPORTS, TEAM_SPORTS } from "../constants/sports";
import SportSection from "./admin/SportSection";
import TeamAdmin from "./admin/TeamAdmin";
import "./Admin.css";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5001"
    : "https://kaushik-olympics-backend.mshik3.workers.dev");

interface LoginData {
  username: string;
  password: string;
}

interface Competitor {
  name: string;
  totalScore: number;
  scores: { sport: string; score: number }[];
}

type PendingUpdate = { name: string; sport: string; points: number };

interface TeamsState {
  names: { A: string; B: string };
  members: { A: string[]; B: string[] };
}

const Admin: React.FC = () => {
  const [participants, setParticipants] = useState<string[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    process.env.NODE_ENV === "development" ||
      process.env.REACT_APP_SKIP_ADMIN_LOGIN === "true"
  );
  const [loginData, setLoginData] = useState<LoginData>({
    username: "",
    password: "",
  });
  const [scoresData, setScoresData] = useState<Competitor[]>([]);
  const [teams, setTeams] = useState<TeamsState | null>(null);

  useEffect(() => {
    async function fetchAll() {
      try {
        const response = await fetch(`${API_BASE}/api/scores`);
        const data: Competitor[] = await response.json();
        setScoresData(data);
        setParticipants(data.map((p) => p.name));
      } catch (error) {
        console.error("Error fetching participants:", error);
      }
    }
    fetchAll();
  }, []);

  useEffect(() => {
    async function fetchTeams() {
      try {
        const response = await fetch(`${API_BASE}/api/teams`);
        const data: TeamsState = await response.json();
        setTeams(data);
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    }
    fetchTeams();
  }, []);

  const handleLoginChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData({ ...loginData, [name]: value });
  };

  const handleLogin = (e: FormEvent) => {
    e.preventDefault();
    if (loginData.username === "admin" && loginData.password === "password") {
      setIsAuthenticated(true);
    } else {
      alert("Invalid credentials");
    }
  };

  const perSportScores: Record<
    string,
    Record<string, number>
  > = React.useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    for (const sport of SPORTS) {
      map[sport] = {};
    }
    for (const comp of scoresData) {
      for (const s of comp.scores) {
        if (!map[s.sport]) map[s.sport] = {};
        map[s.sport][comp.name] = s.score;
      }
    }
    return map;
  }, [scoresData]);

  const saveBulk = async (updates: PendingUpdate[]) => {
    if (updates.length === 0) return;
    const response = await fetch(`${API_BASE}/api/scores/bulk`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    if (response.ok) {
      // refresh scores
      const refreshed = await fetch(`${API_BASE}/api/scores`);
      const data: Competitor[] = await refreshed.json();
      setScoresData(data);
      window.dispatchEvent(new Event("scores-updated"));
      alert("Scores updated");
    } else {
      alert("Failed to update scores");
    }
  };

  // Handler to save only the updates for a specific sport
  const handleSportSave = async (updates: PendingUpdate[]) => {
    await saveBulk(updates);
  };

  const resetAllScores = async () => {
    const confirmed = window.confirm(
      "Reset all scores to zero for all participants? This cannot be undone."
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_BASE}/api/scores/reset`, {
        method: "POST",
      });
      if (!res.ok) throw new Error("Failed to reset scores");
      const refreshed = await fetch(`${API_BASE}/api/scores`);
      const data: Competitor[] = await refreshed.json();
      setScoresData(data);
      window.dispatchEvent(new Event("scores-updated"));
      alert("All scores reset");
    } catch (e) {
      alert("Failed to reset scores");
    }
  };

  return (
    <div className="admin-container">
      {!isAuthenticated ? (
        <form onSubmit={handleLogin}>
          <div>
            <label>Username:</label>
            <input
              type="text"
              name="username"
              value={loginData.username}
              onChange={handleLoginChange}
            />
          </div>
          <div>
            <label>Password:</label>
            <input
              type="password"
              name="password"
              value={loginData.password}
              onChange={handleLoginChange}
            />
          </div>
          <button type="submit">Login</button>
        </form>
      ) : (
        <div>
          {isAuthenticated && (
            <div className="sports-grid" style={{ marginBottom: 12 }}>
              <TeamAdmin
                participants={participants}
                teams={teams || undefined}
                onTeamsChange={(t) => setTeams(t)}
              />
            </div>
          )}
          <div className="admin-actions">
            <button onClick={resetAllScores} className="btn-secondary">
              Reset All Scores
            </button>
          </div>
          <div className="sports-grid">
            {SPORTS.map((sport) => (
              <SportSection
                key={sport}
                sport={sport}
                participants={participants}
                currentSportScores={perSportScores[sport] || {}}
                isTeamSport={TEAM_SPORTS.includes(sport)}
                teams={
                  teams || {
                    names: { A: "Team A", B: "Team B" },
                    members: { A: [], B: [] },
                  }
                }
                onSave={handleSportSave}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
